import { renderChart } from './chart';
import { MASK_MODE_TYPES, MASK_TYPES, LayoutSizeType, TYPES, SHAPES } from './constants';
import { renderDebug, renderFallback } from './error';
import { renderCol, renderRow } from './flex';
import { renderFrame } from './frame';
import { renderGroup } from './group';
import { renderImage } from './image';
import { renderLine } from './line';
import { renderPath } from './path';
import { renderShape } from './shape';
import { renderText } from './text';
import { renderVideo } from './video';
import { applyEffects, computeEffectMask, EFFECT_MASKS } from './effects';

import { emptyImg } from './utils/image';
import { RENDER_CONTEXT_TYPES } from './context/createRenderContext';

// 预编译的渲染函数映射表，避免运行时switch开销
const RENDER_MAP = {
  [TYPES.text]: renderText,
  [TYPES.image]: renderImage,
  [TYPES.row]: renderRow,
  [TYPES.path]: renderPath,
  [TYPES.col]: renderCol,
  [TYPES.shape]: renderShape,
  [TYPES.group]: renderGroup,
  [TYPES.frame]: renderFrame,
  [TYPES.line]: renderLine,
  [TYPES.video]: renderVideo,
  [TYPES.chart]: renderChart,
};

export { EFFECT_MASKS, computeEffectMask };

export function renderNode(renderContext, node) {
  // 提前检查并缓存条件，减少重复计算
  const isExportMode = renderContext.mode === RENDER_CONTEXT_TYPES.export;
  const hasViewportCheck = renderContext.isNodeInViewport;

  // 快速路径：导出模式或在视口内
  if (!isExportMode && hasViewportCheck && !hasViewportCheck(node)) return;

  // 显式检查可见性
  if (node.visible === false) return;

  applyEffects(renderContext, node, () => renderNodeRaw(renderContext, node));

  // 仅在非导出模式且显式开启 debug 下渲染调试信息
  if (!isExportMode && renderContext.debug) {
    renderDebug(renderContext, node);
  }
}

function renderNodeRaw(renderContext, node) {
  // 使用映射表替代switch，提升查找性能
  const renderFn = RENDER_MAP[node.type];

  if (renderFn) {
    try {
      renderFn(renderContext, node);
    } catch (e) {
      console.error(`Failed to render node ${node.id}:`, e);
      renderFallback(renderContext, node);
    }
  } else {
    // 未知类型的节点类型，渲染fallback
    console.warn(`Unknown node type: ${node.type}`);
    renderFallback(renderContext, node);
  }
}

export function onDragStart(context, e, type, options, callback) {
  e.target.style.cursor = 'move';
  e.dataTransfer.setDragImage(emptyImg, 0, 0);
  const data = {
    type,
    ...options,
  };
  const node = createNodeByType(data);
  const { interactive } = context.providers;
  interactive.createNode = node;
  interactive.createNodeCallback = callback;
}

export function createNodeByType(options) {
  switch (options.type) {
    case TYPES.text:
      return {
        type: options.type,
        x: 0,
        y: 0,
        w: 200,
        h: 100,
        height: LayoutSizeType.hug,
        text: 'ABCDED',
        fontFamily: 'Poppins',
        fill: '#111',
        fontSize: 32,
        ...options,
      };
    case TYPES.group:
      return {
        type: options.type,
        x: 0,
        y: 0,
        w: 100,
        h: 100,
        ...options,
      };
    case TYPES.image:
      return {
        type: options.type,
        x: 0,
        y: 0,
        w: 100,
        h: 100,
        src: options.src || '',
        ...options,
      };
    case TYPES.shape:
      return {
        type: options.type,
        x: 0,
        y: 0,
        w: 100,
        h: 100,
        shape: options.shape || SHAPES.rect,
        fill: options.fill || '#000000',
        stroke: options.stroke || '#000000',
        strokeWidth: options.strokeWidth || 0,
        ...options,
      };
    case TYPES.line:
      return {
        type: options.type,
        x: 0,
        y: 0,
        w: 200,
        h: 10,
        ...options,
      };
  }
}

/**
 * Mask serialization protocol for backward compatibility
 * Combines mask type and mask mode into a single string format
 */
export function serializeMask(mask, maskMode) {
  // Handle legacy single mask values
  if (!mask) return null;

  // If maskMode is not specified, use legacy format
  if (!maskMode || maskMode === MASK_MODE_TYPES.clip) {
    return mask;
  }

  // New format: "maskType:maskMode"
  return `${mask}:${maskMode}`;
}

export function deserializeMask(serializedMask) {
  if (!serializedMask) {
    return { mask: null, maskMode: MASK_MODE_TYPES.clip };
  }

  // Check if it's the new format with colon separator
  if (typeof serializedMask === 'string' && serializedMask.includes(':')) {
    const [mask, maskMode] = serializedMask.split(':');
    return {
      mask: mask || null,
      maskMode: maskMode || MASK_MODE_TYPES.clip,
    };
  }

  // Legacy format - just mask type, default to normal mode
  return {
    mask: serializedMask,
    maskMode: MASK_MODE_TYPES.clip,
  };
}

/**
 * Helper to get effective mask configuration from a node
 */
export function getNodeMaskConfig(node) {
  if (!node) return { mask: null, maskMode: MASK_MODE_TYPES.clip };

  // If node has separate mask and maskMode properties, use them directly
  if (node.mask !== undefined || node.maskMode !== undefined) {
    return {
      mask: node.mask || null,
      maskMode: node.maskMode || MASK_MODE_TYPES.clip,
    };
  }

  // If node has serialized mask string, deserialize it
  if (node.serializedMask) {
    return deserializeMask(node.serializedMask);
  }

  // Fallback to no mask
  return { mask: null, maskMode: MASK_MODE_TYPES.clip };
}

export function renderContent(stageContext, node, renderNodes) {
  // Fast path: no masks -> draw directly
  if (!Array.isArray(renderNodes)) return;

  const len = renderNodes.length;
  let hasAlphaMask = false;

  // Inline check to avoid .some() overhead
  for (let i = 0; i < len; i++) {
    if (renderNodes[i] && renderNodes[i].mask) {
      hasAlphaMask = true;
      break;
    }
  }

  if (!hasAlphaMask) {
    for (let i = 0; i < len; i++) {
      renderNode(stageContext, renderNodes[i]);
    }
    return;
  }

  const w = node?.w || 0;
  const h = node?.h || 0;

  // Early exit for zero-size nodes
  if (w <= 0 || h <= 0) return;

  const { canvas: canvasProvider, scale: scaleProvider } = stageContext.providers;
  const deviceDPR = stageContext.dpr || window.devicePixelRatio || 1;
  const safeDPR = deviceDPR < 2 ? 2 : deviceDPR;
  const isScaling = scaleProvider.scaling;
  const highQuality = stageContext.highQuality;
  const scale = scaleProvider.scale || 1;

  // Effective DPR for offscreen rendering to reduce aliasing at zoom
  // - While scaling interactively, keep it modest for performance
  // - When not scaling (or in highQuality), use scale-aware DPR with a sensible cap
  let effectiveDpr = isScaling ? deviceDPR : safeDPR * scale;
  if (highQuality && effectiveDpr < safeDPR * scale) {
    effectiveDpr = safeDPR * scale;
  }
  // Clamp between 1 and 4
  if (effectiveDpr > 4) effectiveDpr = 4;
  else if (effectiveDpr < 1) effectiveDpr = 1;

  // Clamp DPR to avoid exceeding browser canvas maximums
  const maxDim = canvasProvider?.options?.maxOffscreenDimension || 16384;
  const plannedW = Math.ceil(w * effectiveDpr) || 1;
  const plannedH = Math.ceil(h * effectiveDpr) || 1;

  if (plannedW > maxDim || plannedH > maxDim) {
    const fitScale = maxDim / (plannedW > plannedH ? plannedW : plannedH);
    effectiveDpr = effectiveDpr * fitScale;
    if (effectiveDpr < 1) effectiveDpr = 1;
  }

  // Create an offscreen accumulation canvas in frame-local coordinates with DPR scaling
  const accCanvasInfo = canvasProvider.createOffscreenCanvas(w, h, effectiveDpr);
  const accCanvas = accCanvasInfo.canvas;
  const accCtx = accCanvasInfo.context;

  // Favor high-quality resampling inside offscreen
  accCtx.imageSmoothingEnabled = true;
  accCtx.imageSmoothingQuality = 'high';

  const offscreenStage = { ...stageContext, context: accCtx, isNodeInViewport: () => true };

  // Ensure a clean slate
  accCtx.clearRect(0, 0, accCanvas.width, accCanvas.height);

  // Draw children into the accumulator, applying alpha masks when encountered
  for (let i = 0; i < len; i++) {
    const child = renderNodes[i];
    if (!child) continue;

    const childMask = child.mask;
    if (childMask) {
      // Inline deserializeMask logic to avoid function call overhead
      let maskType = null;
      let maskMode = MASK_MODE_TYPES.clip;

      if (typeof childMask === 'string') {
        const colonIdx = childMask.indexOf(':');
        if (colonIdx !== -1) {
          maskType = childMask.substring(0, colonIdx) || null;
          maskMode = childMask.substring(colonIdx + 1) || MASK_MODE_TYPES.clip;
        } else {
          maskType = childMask;
        }
      }

      if (maskType === MASK_TYPES.alpha) {
        accCtx.globalCompositeOperation =
          maskMode === MASK_MODE_TYPES.intersect ? 'destination-in' : 'destination-out';
        renderNode(offscreenStage, child);
        accCtx.globalCompositeOperation = 'source-over';
      } else {
        renderNode(offscreenStage, child);
      }
    } else {
      renderNode(offscreenStage, child);
    }
  }

  // Blit accumulated result back to the main context inside the frame
  const ctx = stageContext.context;
  if (highQuality) {
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
  }

  ctx.drawImage(accCanvas, 0, 0, accCanvas.width, accCanvas.height, 0, 0, w, h);

  // Release pooled canvas if applicable
  if (typeof accCanvas.release === 'function') {
    accCanvas.release();
  }
}
